import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import BlockIcon from '@mui/icons-material/Block';
import LogoutIcon from '@mui/icons-material/Logout';
import { BUTTON_BORDER_RADIUS } from 'constants/borderRadius';

/**
 * @typedef {object} MFABlockedProps
 * @property {Function} onSignOut - Callback to sign out and return to login
 */

/**
 * MFA blocked message component.
 * Displayed when user is required to have MFA but has no phone number configured.
 * This is a security gate - users cannot proceed without MFA.
 * Uses subtle styling since the LoginCard shows error state with red liquid glass.
 *
 * @param {MFABlockedProps} props - Component props
 * @returns {React.ReactElement}
 */
const MFABlocked = ({ onSignOut }) => {
    return (
        <Box sx={{ mt: 2 }}>
            {/* Subtle error message with icon */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    mb: 3,
                }}
            >
                <BlockIcon
                    color="error"
                    sx={{ mt: 0.25, fontSize: '1.25rem' }}
                />
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="body2"
                        color="error"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                        Access Denied
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6, mb: 1 }}
                    >
                        Your organization requires multi-factor authentication (MFA) to access this system.
                        However, your account does not have a phone number configured for MFA.
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500, lineHeight: 1.6 }}
                    >
                        Please contact your administrator to configure MFA for your account.
                    </Typography>
                </Box>
            </Box>

            <Button
                id="mfa-blocked-signout-btn"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={onSignOut}
                startIcon={<LogoutIcon />}
                sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
            >
                Sign Out
            </Button>
        </Box>
    );
};

MFABlocked.propTypes = {
    onSignOut: PropTypes.func.isRequired,
};

export default MFABlocked;
